import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Avatar, Box, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";

import { FormattedDateTime, formatDateTime } from "@/shared/datetime";
import { CommentRecord } from "@/shared/domain/comments";
import { getAssetPath } from "@/shared/environment";

import { RobotoIconButton } from "../RobotoIconButton";
import { RobotoLink } from "../RobotoLink";
import { RobotoMarkdown } from "../RobotoMarkdown";
import { RobotoTooltip } from "../RobotoTooltip";

import { CommentForm } from "./CommentForm";

const ROBOTO_PLATFORM_USER_IDS = ["support@roboto.ai", "system@roboto.ai"];

function trimComment(text: string) {
  return text.length > 300 ? text.slice(0, 300) + "..." : text;
}

interface CommentProps {
  comment: CommentRecord;
  currentUserId: string;
  avatarUrl: string | null;
  onDelete?: (comment: CommentRecord) => void;
  onUpdate?: (comment: CommentRecord) => void;
  summaryView?: boolean;
}

export function Comment(props: CommentProps) {
  const {
    comment,
    currentUserId,
    avatarUrl,
    onDelete,
    onUpdate,
    summaryView = false,
  } = props;

  const theme = useTheme();
  const [editMode, setEditMode] = useState(false);

  const time = new Date(comment.modified);
  const humanTime = time
    ? dayjs.duration(dayjs(time).diff(dayjs())).humanize(true)
    : "Never";
  const displayText = summaryView
    ? trimComment(comment.comment_text)
    : comment.comment_text;

  // If in edit mode, show the form
  if (editMode) {
    return (
      <CommentForm
        entityId={comment.entity_id}
        entityType={comment.entity_type}
        onSubmit={(updatedComment) => {
          onUpdate?.(updatedComment);
          setEditMode(false);
        }}
        onCancel={() => setEditMode(false)}
        commentToEdit={comment}
      />
    );
  }

  // Otherwise show the comment display
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        marginBottom: theme.spacing(2),
        gap: theme.spacing(0.5),
        position: "relative",
        ":hover .hoverButtons": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        {ROBOTO_PLATFORM_USER_IDS.includes(comment.created_by) ? (
          <Avatar
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: 38, height: 38 }}
          >
            <img
              src={getAssetPath("iconWhite.svg")}
              style={{ width: "40px", height: "40px" }}
              alt="Roboto Logo"
            />
          </Avatar>
        ) : (
          <Avatar
            src={avatarUrl ?? ""}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: 38, height: 38 }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          marginLeft: theme.spacing(2),
          flex: 1,
          a: {
            textDecoration: "none",
            ":hover": {
              textDecoration: "underline",
            },
          },
        }}
      >
        <Typography variant="caption" component="span">
          <span style={{ fontWeight: 500 }}>
            {ROBOTO_PLATFORM_USER_IDS.includes(comment.created_by) ? (
              <>{comment.created_by}</>
            ) : (
              <RobotoLink
                to={`/users/${comment.created_by}`}
                sx={{ color: theme.palette.text.primary }}
              >
                {comment.created_by}
              </RobotoLink>
            )}
          </span>
          &nbsp;{" "}
          <span
            style={{ color: "#888" }}
            title={formatDateTime(comment.modified) + " (" + humanTime + ")"}
          >
            <FormattedDateTime value={comment.modified} showTooltip={false} />
            {summaryView && (
              <>
                {" on "}
                <RobotoLink
                  to={`/${comment.entity_type}s/${comment.entity_id}`}
                  sx={{ color: "#888" }}
                >
                  {comment.entity_id}
                </RobotoLink>{" "}
              </>
            )}
            {comment.created !== comment.modified ? "(edited)" : ""}
          </span>
        </Typography>
        <Box
          sx={{
            fontSize: "0.9rem",
            a: { color: theme.palette.primary.main },
          }}
        >
          <RobotoMarkdown content={displayText} />
        </Box>
      </Box>
      {comment.created_by === currentUserId && !summaryView && (
        <Box
          className="hoverButtons"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "flex-start",
            opacity: 0,
            transition: "opacity 0.2s ease",
          }}
        >
          <RobotoIconButton
            eventName={"EditCommentClicked"}
            eventProperties={{ commentId: comment.comment_id }}
            sx={{ marginRight: theme.spacing(1) }}
            onClick={() => setEditMode(true)}
          >
            <RobotoTooltip title="Edit">
              <EditIcon fontSize="small" color="primary" />
            </RobotoTooltip>
          </RobotoIconButton>
          {onDelete && (
            <RobotoIconButton
              eventName={"DeleteCommentClicked"}
              eventProperties={{ commentId: comment.comment_id }}
              onClick={() => onDelete(comment)}
            >
              <RobotoTooltip title="Delete">
                <DeleteIcon fontSize="small" color="primary" />
              </RobotoTooltip>
            </RobotoIconButton>
          )}
        </Box>
      )}
    </Box>
  );
}
